import { NgClass } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { EntityStatus } from '@core';
import { AvsDateRangeIds, getAvsDateRange } from '@ui';
import { map, of, switchMap } from 'rxjs';

import type { DeviceData } from '../../models/devices';
import { DeviceService } from '../../services/device.service';

type DeviceBadgeStatus = 'online' | 'offline' | 'paused' | 'no-data' | 'archived';

const BADGE_LABEL: Record<DeviceBadgeStatus, string> = {
  archived: 'Archived',
  'no-data': 'No data',
  offline: 'Offline',
  online: 'Online',
  paused: 'Paused',
};

@Component({
  imports: [NgClass],
  selector: 'avs-device-status-badge',
  styleUrl: './device-status-badge.component.scss',
  templateUrl: './device-status-badge.component.html',
})
export class DeviceStatusBadgeComponent {
  device = input.required<DeviceData>();

  protected readonly BADGE_LABEL = BADGE_LABEL;

  protected status = toSignal(
    toObservable(this.device).pipe(
      switchMap(device => {
        if (device.status === EntityStatus.Archived) {
          return of<DeviceBadgeStatus>('archived');
        }
        if (device.status === EntityStatus.Paused) {
          return of<DeviceBadgeStatus>('paused');
        }

        const { start, end } = getAvsDateRange(AvsDateRangeIds.LAST_24_HOURS);

        return this.deviceService.getChartsData({
          data: {
            fromDateTimeUtc: start!.toISOString(),
            point: 'connected',
            toDateTimeUtc: end!.toISOString(),
          },
          deviceId: device.id,
        }).pipe(
          map(chart => {
            if (!chart?.timeseries?.length) {
              return 'no-data' as DeviceBadgeStatus;
            }

            const latest = chart.timeseries.reduce((acc, p) =>
              new Date(p.timeStamp) > new Date(acc.timeStamp) ? p : acc,
            );

            return (latest.value === 1 ? 'online' : 'offline') as DeviceBadgeStatus;
          }),
        );
      }),
    ),
    { initialValue: 'no-data' as DeviceBadgeStatus },
  );

  private deviceService = inject(DeviceService);
}
